import { type ReportWidgetCatalogItem } from 'src/api/generated';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';
import {
  PiChartBar,
  PiFunnel,
  PiHouseLine,
  PiRows,
  PiUsers,
} from 'react-icons/pi';
import {
  REPORT_WIDGET_KIND,
  toReportWidgetKind,
} from '../utils/report-dashboard';
import { getSeriesColor } from './reports-dashboard.utils';

/**
 * Compact preview used in the widget catalog.
 */
export const WidgetThumbnail: React.FC<{
  kind: ReportWidgetCatalogItem['kind'];
}> = ({ kind }) => {
  const { t } = useI18n();
  const iconClassName = 'h-5 w-5 text-neutral-700';
  const widgetKind = toReportWidgetKind(kind);

  return (
    <div className="rounded-2xl border border-neutral-200 bg-gradient-to-br from-white via-neutral-50 to-secondary-100 p-4">
      {widgetKind === REPORT_WIDGET_KIND.FUNNEL_STATUS_FUNNEL && (
        <div className="space-y-2">
          <PiFunnel className={iconClassName} />
          <div className="space-y-2">
            {[
              [55, 35],
              [42, 28],
              [30, 18],
            ].map((segments, index) => (
              <div key={index} className="rounded-full bg-neutral-100 p-0.5">
                <div className="flex h-3 overflow-hidden rounded-full">
                  {segments.map((segmentWidth, segmentIndex) => (
                    <div
                      key={`${index}-${segmentIndex}`}
                      style={{
                        width: `${segmentWidth}%`,
                        backgroundColor: getSeriesColor(segmentIndex),
                      }}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {widgetKind ===
        REPORT_WIDGET_KIND.FUNNEL_STATUS_FIRST_ENTRY_TIMESERIES && (
        <div className="space-y-2">
          <PiChartBar className={iconClassName} />
          <div className="flex h-16 items-end gap-1">
            {[
              [12, 12],
              [20, 28],
              [14, 20],
              [24, 34],
              [18, 24],
            ].map((segments, index) => (
              <div
                key={index}
                className="flex flex-1 flex-col justify-end overflow-hidden rounded-t-lg bg-neutral-100"
              >
                {segments.map((segmentHeight, segmentIndex) => (
                  <div
                    key={`${index}-${segmentIndex}`}
                    style={{
                      height: segmentHeight,
                      backgroundColor: getSeriesColor(segmentIndex),
                    }}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      )}
      {widgetKind === REPORT_WIDGET_KIND.PROPERTY_TYPE_DISTRIBUTION && (
        <div className="space-y-2">
          <PiHouseLine className={iconClassName} />
          <div className="space-y-2">
            {[76, 52, 34].map((width, index) => (
              <div
                key={width}
                className="overflow-hidden rounded-full bg-neutral-100"
              >
                <div
                  className="h-3 rounded-full"
                  style={{
                    width: `${width}%`,
                    backgroundColor: getSeriesColor(index),
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      )}
      {widgetKind === REPORT_WIDGET_KIND.PROPERTY_INTEREST_RANKING && (
        <div className="space-y-2">
          <PiRows className={iconClassName} />
          <div className="space-y-2 rounded-xl border border-neutral-200 bg-white p-2">
            <div className="grid grid-cols-[1fr_auto] gap-1 text-[10px] uppercase tracking-wide text-neutral-500">
              <span>{t('reports.dashboard.thumbnail.property')}</span>
              <span className="text-right">
                {t('reports.dashboard.thumbnail.consultations')}
              </span>
            </div>
            {[1, 2, 3].map((rank) => (
              <div
                key={rank}
                className="flex items-center justify-between rounded-xl bg-neutral-100 px-3 py-2 text-sm text-neutral-700"
              >
                <span>
                  {t('reports.dashboard.thumbnail.property')} {rank}
                </span>
                <span>{10 - rank}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {widgetKind === REPORT_WIDGET_KIND.USER_ASSIGNMENT_RANKING && (
        <div className="space-y-2">
          <PiUsers className={iconClassName} />
          <div className="rounded-xl border border-neutral-200 bg-white p-2">
            <div className="grid grid-cols-[1fr_repeat(3,minmax(0,1fr))] gap-1 text-[10px] text-neutral-500">
              <span>{t('reports.dashboard.thumbnail.user')}</span>
              <span className="text-right">A</span>
              <span className="text-right">B</span>
              <span className="text-right">T</span>
            </div>
            {[
              ['Ana', 8, 3, 11],
              ['Luis', 6, 2, 8],
            ].map((row) => (
              <div
                key={row[0]}
                className="grid grid-cols-[1fr_repeat(3,minmax(0,1fr))] gap-1 text-[10px] text-neutral-700"
              >
                <span>{row[0]}</span>
                <span className="text-right">{row[1]}</span>
                <span className="text-right">{row[2]}</span>
                <span className="text-right font-semibold">{row[3]}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
